import React, { useEffect, useState } from "react";
import styles from "@/admin/pages/homepage/HomePage.module.scss";
import { useNavigate } from "react-router-dom";
import CustomButton from "@/commons/components/buttons/customButton/CustomButton";
import ApiInterface from "@/commons/api/ApiInterface";

type ButtonConfig = {
    title: string;
    page: string;
    permission: string;
    hide?: boolean;
};

type SectionProps = {
    sectionTitle: string;
    sectionPermission: string;
    color: string; // es: "#ffa67f"
    image: React.FC<React.SVGProps<SVGSVGElement>>;
    buttons: ButtonConfig[];
};

const Section: React.FC<SectionProps> = ({
                                             sectionTitle,
                                             sectionPermission,
                                             color,
                                             image: ImageComponent,
                                             buttons
                                         }) => {
    const navigate = useNavigate();
    const [hasSectionPermission, setHasSectionPermission] = useState<boolean | null>(null);
    const [buttonPermissions, setButtonPermissions] = useState<Record<string, boolean>>({});

    useEffect(() => {
        ApiInterface.hasPermission(sectionPermission).then(setHasSectionPermission);

        const fetchPermissions = async () => {
            const results = await Promise.all(
                buttons.map(async (btn) => {
                    const result = await ApiInterface.hasPermission(btn.permission);
                    return [btn.permission, result] as const;
                })
            );
            setButtonPermissions(Object.fromEntries(results));
        };

        fetchPermissions();
    }, [sectionPermission, buttons]);

    if (hasSectionPermission === null || !hasSectionPermission) return null;

    return (
        <div
            className={styles.subCategory}
            style={{
                backgroundColor: color,
                boxShadow: `0 0 10px ${color}`
            }}
        >
            <ImageComponent />
            <div className={styles.categoryContent}>
                <h2>{sectionTitle}</h2>
                <div className={styles.buttonGroup}>
                    {buttons.map((btn, i) => {
                        const hasPerm = buttonPermissions[btn.permission];
                        if (hasPerm === undefined) return null;
                        if (btn.hide && !hasPerm) return null;

                        return (
                            <div className={styles.button} key={i}>
                                <CustomButton
                                    onClick={() => navigate(btn.page)}
                                    disabled={!hasPerm}
                                >
                                    {btn.title}
                                </CustomButton>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Section;